import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CardSetupForm from '../../Components/Stripe/CardSetupForm';
import {
  createCustomerPortalSetupIntent,
  fetchCustomerAppointments,
  fetchCustomerInfo,
  removeCustomerPaymentMethod,
  updateCustomerPaymentMethod,
} from '../../services/CustomerPortal';
import Page from '../../Components/Page/BookingPage';
import { useNavigate } from 'react-router-dom';
import useAppointmentsStore from './customerAppointmentsStore';
import {useIsSmallScreen} from '../../hooks/useIsSmallScreen';
import styles from './info.module.css';
import { LuPen, LuTrash2 } from 'react-icons/lu';
import Appointments from "./Appointments"

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');
const domain = process.env.REACT_APP_DOMAIN || 'http://localhost:3000';
const checkoutFormID = 'checkout-payment-form';

function getStripeAppearance() {
  const root = getComputedStyle(document.documentElement);
  const token = (name, fallback) => root.getPropertyValue(name).trim() || fallback;

  return {
    theme: 'stripe',
    variables: {
      colorPrimary: token('--action-primary', '#b04e4e'),
      colorBackground: token('--background-50', '#f9ecec'),
      colorText: token('--text-900', '#270c0c'),
      borderRadius: token('--radius-sm', '8px'),
      fontFamily: 'Montserrat, system-ui, sans-serif',
    },
  };
}

function CustomerPortalInfo() {
  const [customerInfo, setCustomerInfo] = useState(null);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [formSaving, setFormSaving] = useState(false);
  const [formComplete, setFormComplete] = useState(false);
  const isMobile = useIsSmallScreen(); 
  const navigate = useNavigate();
  const { setAppointments } = useAppointmentsStore();
  const [editingAppointments, setEditingAppointments] = useState(false);

  // Only the initial load shows the full-page loading state
  const loadCustomerInfo = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const info = await fetchCustomerInfo();
      setCustomerInfo(info);

      const customerId = Number(info?.customer_id ?? info?.customer?.id ?? 0);
      if (customerId) {
        const appointments = await fetchCustomerAppointments(customerId);
        setAppointments(appointments);
      } else {
        setAppointments([]);
      }
    } catch (error) {
      console.error('Failed to fetch customer info:', error);
      setAppointments([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [setAppointments]);

  useEffect(() => {
    loadCustomerInfo();
  }, [loadCustomerInfo]);

  const closeForm = () => {
    setShowUpdateForm(false);
    setFormComplete(false);
    setFormSaving(false);
  };

  const customer = customerInfo?.customer ?? null;
  const cardOnFile = customer?.card_on_file ?? 'Not available';
  const avatar = customer?.name
    ? customer.name.split(' ').map((n) => n[0]).join('').slice(0, 2)
    : '';

  const stripeOptions = useMemo(
    () => ({
      mode: 'setup',
      currency: 'usd',
      paymentMethodTypes: ['card'],
      appearance: getStripeAppearance(),
    }),
    []
  );

  const handleRemoveCard = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to remove the card on file? This action cannot be undone.'
    );
    if (!confirmed) return;

    setRemoving(true);
    setRemoveError('');

    try {
      await removeCustomerPaymentMethod();
      await loadCustomerInfo({ silent: true });
    } catch (error) {
      setRemoveError(error.message || 'Unable to remove card on file.');
    } finally {
      setRemoving(false);
    }
  };

  const renderContent = () => {
    if (loading) return <p>Loading...</p>;
    if (!customer) return <p>No customer information available.</p>;

    const handleAppointmentsClick = () => {
      const appointments = useAppointmentsStore.getState().appointments;
      setEditingAppointments(!editingAppointments)
      setShowUpdateForm(false);
    };

    return (
      <>
        <div className={isMobile ? `${styles.container} ${styles.mobileContainer}` : styles.container}>
          <div className={styles.infoContainer}>
            <div className={styles.headerContainer}>
              <div className={styles.avatar}>{avatar}</div>
              {customer.name && <h2 className={styles.name}>{customer.name}</h2>}
            </div>

            <div className={styles.detailsContainer}>
              <div className={styles.detailItem}>
                <h3>Email:</h3>
                <p>{customer.email || 'Not available'}</p>
              </div>

              <div className={styles.detailItem}>
                <h3>Phone:</h3>
                <p>{customer.phone || 'Not available'}</p>
              </div>

              <div className={styles.detailItem}>
                <h3>Card on File:</h3>
                <div className={styles.cardDetailsContainer}>
                  <p>{customer.card_on_file || 'Not available'}</p>
                  <div className={styles.cardActionsContainer}>
                    <button onClick={() => {
                        setShowUpdateForm(true)
                        setEditingAppointments(false)
                      }}
                    >
                      <LuPen />
                    </button>
                    <button
                      onClick={handleRemoveCard}
                      disabled={removing || cardOnFile === 'Not available'}
                    >
                      <LuTrash2 />
                    </button>
                  </div>
                </div>
                {removeError && <p style={{ color: 'var(--danger)' }}>{removeError}</p>}
              </div>
            </div>
            <button 
              onClick={handleAppointmentsClick}
              className={styles.appointmentsButton}
            >
              {`${editingAppointments ? 'Hide' : 'View'} Appointments`}
            </button>

          </div>


            {(!isMobile || editingAppointments || showUpdateForm) && (
              <div className={styles.actionsContainer}>
              {showUpdateForm && (
                <>
                <Elements stripe={stripePromise} options={stripeOptions}>
                  <CardSetupForm
                    returnUrl={`${domain}/customer-portal-info`}
                    onBeforeConfirm={async () => {
                      const portalSetup = await createCustomerPortalSetupIntent();
                      if (!portalSetup?.client_secret) {
                        throw new Error('No client secret returned from the customer portal setup service.');
                      }
                      return portalSetup;
                    }}
                    onSuccess={async ({ setupIntent }) => {
                      await updateCustomerPaymentMethod(setupIntent.id);
                      closeForm();
                      await loadCustomerInfo({ silent: true });
                    }}
                    showSubmitButton={false}
                    formId={checkoutFormID}
                    onSavingChange={setFormSaving}
                    onValidityChange={setFormComplete}
                  />
                </Elements>

                <div className={styles.actionsContainerActionRow}>
                  <button onClick={closeForm} className={styles.cancelButton}>
                    Cancel
                  </button>
                  <button
                    className={styles.saveButton}
                    form={checkoutFormID}
                    type="submit"
                    disabled={formSaving || !formComplete}
                  >
                    {formSaving ? 'Saving…' : 'Save Card'}
                  </button>
                </div>
                </>
              )}
              {editingAppointments && <Appointments />}
            </div>)}
        </div>
      </>
    );
  };

  return (
    <Page
      grid={false}
      title="Customer Portal"
      subtitle="View and edit your information on file."
      wide
    >
      {renderContent()}
    </Page>
  );
}

export default CustomerPortalInfo;